import { Howl } from "howler";
import { useEffect, useMemo, useRef, useState } from "react";
import useSound from "use-sound";
import music from "./music2.mp3";
import jumpSound from "./jump1.mp3";
import Ground from "./Ground";
import useKeys from "./keyboard/useKeys";
import Player from "./Player";
import {
  Action,
  entityById,
  findHighestFilledRow,
  isWreckIndestructible,
  Player as PlayerT,
  State,
} from "./reducer";
import Water from "./Water";
import {
  ENEMY_INITIAL_HEALTH,
  GRID_BOTTOM,
  GRID_ROWS,
  GRID_TOP,
  PLAYER_ID,
} from "./constants";
import PlayerProjectile from "./PlayerProjectile";
import Enemy from "./Enemy";
import HealthMeter from "./HealthMeter";
import Wreck from "./Wreck";
import { Container, useTick } from "./react-graphics";
import EnemyProjectile from "./EnemyProjectile";
import HealthPowerUp from "./HealthPowerUp";

type Props = {
  canvasWidth: number;
  canvasHeight: number;
  state: State;
  dispatch: React.Dispatch<Action>;
};

const LEFT_KEYS = ["ArrowLeft", "KeyA"];

const RIGHT_KEYS = ["ArrowRight", "KeyD"];

const JUMP_KEYS = ["KeyW", "ArrowUp"];

const SHOOT_KEYS = ["Space"];

const TIME_STEP = 1 / 120;

const Level = ({ canvasWidth, canvasHeight, state, dispatch }: Props) => {
  useEffect(() => {
    const sound = new Howl({
      src: [music],
      loop: true,
      volume: 0.5,
      autoplay: true,
    });
    return () => {
      sound.stop();
      sound.unload();
    };
  }, []);

  const [playJumpSound] = useSound(jumpSound, { volume: 0.5 });

  const lastTickInfo = useRef({ time: 0, frame: 0 });
  useTick((timeDelta) => {
    const currentTime = lastTickInfo.current.time + timeDelta;
    const targetFrame = Math.floor(currentTime / TIME_STEP);
    for (let i = lastTickInfo.current.frame; i < targetFrame; i++) {
      dispatch({ type: "TICK", timeDelta: TIME_STEP });
    }
    lastTickInfo.current.time += timeDelta;
    lastTickInfo.current.frame = targetFrame;
  });

  useKeys([...LEFT_KEYS, ...RIGHT_KEYS], (_, keysDown) => {
    dispatch({
      type: "MOVE",
      playerId: PLAYER_ID,
      moveIntent:
        (RIGHT_KEYS.some((key) => keysDown.includes(key)) ? 1 : 0) -
        (LEFT_KEYS.some((key) => keysDown.includes(key)) ? 1 : 0),
    });
  });

  useKeys(JUMP_KEYS, (event) => {
    if (event.type === "keydown") {
      playJumpSound();
      dispatch({
        type: "JUMP",
        playerId: PLAYER_ID,
      });
    }
  });

  useKeys(SHOOT_KEYS, (event) => {
    if (event.type === "keydown") {
      dispatch({
        type: "SHOOT",
        playerId: PLAYER_ID,
        direction: { x: 0, y: 1 },
      });
    }
  });

  const player = entityById(PLAYER_ID, state.entities) as PlayerT | undefined;
  const highestFilledRow = useMemo(() => findHighestFilledRow(state), [state]);

  const gridHeight = GRID_TOP - GRID_BOTTOM;
  const gridRowHeight = gridHeight / GRID_ROWS;
  const cameraTargetY = -(200 + (highestFilledRow + 1) * gridRowHeight);
  const [cameraY, setCameraY] = useState(cameraTargetY);
  useTick(() => {
    const c = 0.05;
    setCameraY(cameraY * (1 - c) + cameraTargetY * c);
  });

  return (
    <>
      <HealthMeter
        x={canvasWidth / 2 - 100}
        y={20}
        width={200}
        height={6}
        health={player ? (player as PlayerT).health : 0}
      />
      <Container x={canvasWidth / 2} y={canvasHeight / 2 - cameraY}>
        <Water />
        {state.entities.map((entity) => {
          switch (entity.type) {
            case "projectile": {
              return entity.ownerId === PLAYER_ID ? (
                <PlayerProjectile
                  key={entity.id}
                  x={entity.position.x}
                  y={entity.position.y}
                />
              ) : (
                <EnemyProjectile
                  key={entity.id}
                  x={entity.position.x}
                  y={entity.position.y}
                />
              );
            }
            case "enemy": {
              return (
                <Enemy
                  key={entity.id}
                  x={entity.position.x}
                  y={entity.position.y}
                  damaged={entity.health < 0.7 * ENEMY_INITIAL_HEALTH}
                />
              );
            }
            case "wreck": {
              return (
                <Wreck
                  key={entity.id}
                  x={entity.position.x}
                  y={entity.position.y}
                  indestructible={isWreckIndestructible(entity.id, state)}
                />
              );
            }
            case "health-power-up": {
              return (
                <HealthPowerUp
                  key={entity.id}
                  x={entity.position.x}
                  y={entity.position.y}
                />
              );
            }
            default: {
              return null;
            }
          }
        })}
        <Ground />
        {player && (
          <Player key={player.id} x={player.position.x} y={player.position.y} />
        )}
      </Container>
    </>
  );
};

export default Level;
